import { ChangeDetectionStrategy, Component, ElementRef, Input, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { Organization } from '@app/interfaces/accounts_list';
import { BroadcastMessageService } from '@app/services/broadcast/change-organization/broadcast-message.service';
import { BroadcastMessageType } from '@app/services/broadcast/change-organization/broadcastMessage.model';
import { LoaderService } from '@app/services/loader.service';
import { OrganizationsService } from '@app/services/organizations.service';
import { PresenterModeService } from '@app/services/presenter-mode.service';
import { OrganizationTypes } from '@core/services/organization/organization.interface';
import { UnsubscriberBase } from '@core/services/unsubscriber.base';
import { SharedHelper } from '@shared/helpers/shared.helper';
import { getActiveOrganization } from '@utils/helpers';
import { BehaviorSubject } from 'rxjs';
import { finalize, takeUntil } from 'rxjs/operators';
import { FEATURE_FLAGS } from '@app/infrastructure/feature-flag-manager/feature-flag-list';
import { FeatureFlag } from '@app/infrastructure/feature-flag-manager/feature-flag-decorator';
import { FeatureFlagManagerService } from '@app/infrastructure/feature-flag-manager/feature-flag-manager.service';

@Component({
	selector: 'app-organization-picker',
	templateUrl: './organization-picker.component.html',
	styleUrls: ['./organization-picker.component.scss'],
	changeDetection: ChangeDetectionStrategy.OnPush,
})
export class OrganizationPickerComponent extends UnsubscriberBase implements OnInit {
	@FeatureFlag(FEATURE_FLAGS.ENABLE_OPTIMIZATION_HUB) isEnableOptHub: boolean;

	@Input()
	public set organizations(organizations: Organization[]) {
		if (!organizations || organizations.length === 0) {
			return;
		}
		this._organizations = organizations;
		this.filteredOrganizations = organizations;
		const currentOrganization: Organization = getActiveOrganization(organizations);
		if (!currentOrganization) {
			this.switchOrganization(organizations[0].org_id);
			return;
		}
		this.setOrganizationName(currentOrganization);
	}

	@ViewChild('organizationSearchInput') private _organizationSearchInput: ElementRef;

	public searchTermFormGroup: FormGroup = new FormGroup({
		searchTerm: new FormControl(''),
	});
	public maxResults = 50;
	public filteredOrganizations: Organization[] = [];
	public readonly organizationTypes = OrganizationTypes;

	private _applyIncognito$: BehaviorSubject<boolean> = new BehaviorSubject(false);
	private _currentOrganizationName: string;
	private _organizations: Organization[];

	constructor(
		private organizationsService: OrganizationsService,
		private presenterMode: PresenterModeService,
		private loader: LoaderService,
		private broadcastMessageService: BroadcastMessageService,
		public featureFlagManagerService: FeatureFlagManagerService,
	) {
		super();
	}

	public ngOnInit(): void {
		this.subscribeToIncognitoModeChanges();
		this.searchTermFormGroup.valueChanges.subscribe((value) => {
			this.filteredOrganizations = this.filterOrganizations(this._organizations, value.searchTerm);
		});
	}

	/**
	 * Switch active organization.
	 * Handle API request & after success response => refreshSession (get actual session with all data &
	 * save new active organization in localStorage).
	 * @param {string} org_id
	 */
	public switchOrganization(org_id: number): void {
		this.broadcastMessageService.publishMessage({ type: BroadcastMessageType.organizationChanged, payload: org_id });
		this.loader.showLoader();
		this.organizationsService
			.switchOrganization(org_id.toString())
			.pipe(finalize(() => this.loader.hideLoader()))
			.subscribe(() => {
				const currentOrganization = this._organizations.find((x) => x.org_id === org_id);
				this.setOrganizationName(currentOrganization);
			});
	}

	public onOpenChange(isOpen: boolean): void {
		if (!isOpen) {
			this.searchTermFormGroup.setValue({ searchTerm: '' });
		}
	}

	public filterOrganizations(organizations: Organization[] | undefined, searchTerm: string): Organization[] {
		return searchTerm
			? (organizations?.filter((organization) => {
					return (
						organization.org_id?.toString().includes(searchTerm.toLowerCase()) ||
						organization.org_name?.toLowerCase().includes(searchTerm.toLowerCase())
					);
			  }) as Organization[])
			: organizations;
	}

	public updateFocus(): void {
		if (this._organizationSearchInput) {
			this._organizationSearchInput.nativeElement.focus();
		}
	}

	public get applyIncognito$(): BehaviorSubject<boolean> {
		return this._applyIncognito$;
	}

	public get currentOrganizationName(): string {
		return this._currentOrganizationName;
	}

	public get organizations(): Organization[] {
		return this._organizations;
	}

	private setOrganizationName(organization: Organization): void {
		this._currentOrganizationName = SharedHelper.capitalize(organization.org_name);
		this._currentOrganizationName += ' Organization';
	}

	private subscribeToIncognitoModeChanges(): void {
		this.presenterMode.applyIncognito
			.pipe(takeUntil(this.unsubscribe))
			.subscribe((isApply) => this._applyIncognito$.next(isApply));
	}
}
